import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { Button } from '@/components/ui/Button'
import { Page, PageHeader } from '@/components/ui/Page'
import { NewDocument } from '@/features/documents/NewDocument'
import { EmptyState, ErrorState, Loading } from '@/components/ui/States'
import { cn } from '@/lib/cn'
import { formatDateShort, today } from '@/lib/dates'
import { formatCouple, formatMoney } from '@/lib/format'
import { invoiceStanding, outstanding } from '@/lib/invoicing'
import { describeError } from '@/lib/supabase'
import { useAllInvoices, type InvoiceListItem } from './queries'
import { InvoiceStatusBadge, invoiceNumber, invoiceTitle } from './shared'

type Filter = 'owed' | 'overdue' | 'draft' | 'paid' | 'all'

const FILTERS: Array<{ key: Filter; label: string }> = [
  { key: 'owed', label: 'Owed' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'draft', label: 'Not sent' },
  { key: 'paid', label: 'Paid' },
  { key: 'all', label: 'All' },
]

function matches(invoice: InvoiceListItem, filter: Filter, onDate: string): boolean {
  const standing = invoiceStanding(invoice, onDate)
  switch (filter) {
    case 'owed':
      return standing === 'open' || standing === 'overdue'
    case 'overdue':
      return standing === 'overdue'
    case 'draft':
      return standing === 'draft'
    case 'paid':
      return standing === 'paid'
    case 'all':
      return true
  }
}

const sumCents = (items: InvoiceListItem[]) =>
  items.reduce((sum, invoice) => sum + Math.round(outstanding(invoice) * 100), 0) / 100

/**
 * Every invoice in one ledger: what is owed, what is late, what has not gone
 * out. The Studio Ninja "invoices" screen, without the dashboard tiles.
 */
export function InvoicesPage() {
  const query = useAllInvoices()
  const [filter, setFilter] = useState<Filter>('owed')
  const [params, setParams] = useSearchParams()
  const forJob = params.get('new')
  const [adding, setAdding] = useState(forJob !== null)
  const onDate = today()

  const invoices = useMemo(() => query.data ?? [], [query.data])
  const shown = useMemo(() => invoices.filter((invoice) => matches(invoice, filter, onDate)), [invoices, filter, onDate])
  const owed = useMemo(() => sumCents(invoices.filter((invoice) => matches(invoice, 'owed', onDate))), [invoices, onDate])
  const overdue = useMemo(() => sumCents(invoices.filter((invoice) => matches(invoice, 'overdue', onDate))), [invoices, onDate])

  if (query.isPending) return <Loading label="Loading invoices" />
  if (query.isError) {
    return (
      <Page>
        <ErrorState title="Invoices could not load" detail={describeError(query.error)} onRetry={() => void query.refetch()} />
      </Page>
    )
  }

  return (
    <Page>
      <PageHeader
        eyebrow="Documents"
        title="Invoices"
        meta={
          <>
            <span data-numeric>{formatMoney(owed)} owed</span>
            <span className={overdue > 0 ? 'text-clay' : ''} data-numeric>
              {formatMoney(overdue)} overdue
            </span>
          </>
        }
        actions={
          <Button variant="primary" onClick={() => setAdding(true)}>
            New invoice
          </Button>
        }
      />
      {adding ? (
        <NewDocument
          kind="invoice"
          jobId={forJob}
          onClose={() => {
            setAdding(false)
            if (forJob) setParams({}, { replace: true })
          }}
        />
      ) : null}

      <div role="tablist" aria-label="Show" className="mb-4 flex flex-wrap gap-1 border-b">
        {FILTERS.map((item) => (
          <button
            key={item.key}
            role="tab"
            type="button"
            aria-selected={filter === item.key}
            onClick={() => setFilter(item.key)}
            className={cn(
              '-mb-px border-b px-3 py-2 text-[length:var(--text-2xs)]',
              filter === item.key ? 'border-b-accent font-semibold text-accent' : 'border-b-transparent text-taupe hover:text-ink',
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <EmptyState
          title={invoices.length === 0 ? 'No invoices yet' : 'Nothing here'}
          body={
            invoices.length === 0
              ? 'Click New invoice to make one, or accept a quote and its invoice appears here.'
              : 'Nothing matches this filter.'
          }
        />
      ) : (
        <table className="ledger">
          <thead>
            <tr>
              <th className="label">Invoice</th>
              <th className="label">Client</th>
              <th className="label">Due</th>
              <th className="label">Status</th>
              <th className="label num">Total</th>
              <th className="label num">Owed</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((invoice) => (
              <tr key={invoice.id} className="hover:bg-[var(--color-wash)]">
                <td className="py-2">
                  <Link to={`/invoices/${invoice.id}`} className="underline decoration-[var(--color-rule-strong)] underline-offset-4">
                    {invoiceNumber(invoice.invoice_number)}
                  </Link>
                  <span className="ml-2 text-[length:var(--text-2xs)] text-taupe">{invoiceTitle(invoice.kind)}</span>
                </td>
                <td className="py-2">
                  {invoice.job ? (
                    <Link to={`/weddings/${invoice.job.id}`} className="hover:underline">
                      {formatCouple(invoice.job.primary_contact, invoice.job.partner_contact)}
                    </Link>
                  ) : invoice.contact ? (
                    <span>
                      {[invoice.contact.first_name, invoice.contact.last_name].filter(Boolean).join(' ')}
                      <span className="block text-[length:var(--text-2xs)] text-taupe">No project</span>
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="py-2 whitespace-nowrap">{formatDateShort(invoice.due_date)}</td>
                <td className="py-2"><InvoiceStatusBadge invoice={invoice} /></td>
                <td className="num py-2" data-numeric>{formatMoney(Number(invoice.total))}</td>
                <td className="num py-2" data-numeric>
                  {invoice.status === 'void' ? '—' : formatMoney(outstanding(invoice))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Page>
  )
}
